/**
 * Client-side mirror of the backend service-pricing rules
 * (`backend/src/modules/charge-events/service-pricing.util.ts`).
 *
 * Used for live previews; the backend remains the source of truth for the
 * amounts actually persisted. Keep the two in sync if the rules change.
 */

export type BillingUnit = 'flat' | 'per_unit' | 'per_hour' | 'per_day';

export interface ServicePricingInput {
  price?: number | string | null;
  billingUnit?: string | null;
  unitLabel?: string | null;
  minimumUnits?: number | string | null;
  unitIncrement?: number | string | null;
}

export interface ResolvedServicePricing {
  unitPrice: number;
  quantity: number;
  unitLabel: string;
  amount: number;
}

export const BILLING_UNITS: { value: BillingUnit; label: string; defaultUnitLabel: string }[] = [
  { value: 'flat', label: 'Flat (one-off amount)', defaultUnitLabel: '' },
  { value: 'per_unit', label: 'Per unit', defaultUnitLabel: 'unit' },
  { value: 'per_hour', label: 'Per hour', defaultUnitLabel: 'hour' },
  { value: 'per_day', label: 'Per day', defaultUnitLabel: 'day' },
];

export function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Sensible default unit label when the service has none configured. */
export function defaultUnitLabel(billingUnit?: string | null): string {
  switch (billingUnit) {
    case 'per_hour':
      return 'hour';
    case 'per_day':
      return 'day';
    case 'per_unit':
      return 'unit';
    default:
      return '';
  }
}

export function billingUnitLabel(billingUnit?: string | null): string {
  return BILLING_UNITS.find((u) => u.value === billingUnit)?.label || 'Flat (one-off amount)';
}

/** True when a service is billed by a quantity the user may type (time/units). */
export function isQuantityBilled(billingUnit?: string | null): boolean {
  return billingUnit === 'per_hour' || billingUnit === 'per_day' || billingUnit === 'per_unit';
}

/**
 * True when a Service Type is the managed "Emergency" type. Matched on the
 * stable code or on its label, so a hospital that renamed the type still gets
 * the emergency tariff behaviour (e.g. an overwritable cart price). The label
 * match is only meaningful when the type itself (`code` + `name`) is passed —
 * a bare service-type value is matched on the code alone.
 */
export function isEmergencyServiceType(
  code?: string | null,
  name?: string | null,
): boolean {
  const normalized = String(code ?? '').trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === 'emergency' || normalized.startsWith('emergency_')) return true;
  return String(name ?? '').trim().toLowerCase() === 'emergency';
}

/** Normalizes a service-type code so comparisons are case/space insensitive. */
export function normalizeServiceTypeCode(code?: string | null): string {
  return String(code ?? '').trim().toLowerCase();
}

/**
 * True when a service belongs to the Emergency type — by its own code or via
 * the managed type list (`emergencyTypeCodes` holds the normalized codes of
 * every Service Type labelled Emergency).
 */
export function isEmergencyService(
  serviceType?: string | null,
  emergencyTypeCodes?: ReadonlySet<string>,
): boolean {
  return (
    isEmergencyServiceType(serviceType) ||
    Boolean(emergencyTypeCodes?.has(normalizeServiceTypeCode(serviceType)))
  );
}

/** Resolves billable units + amount, applying minimum-units and hourly rounding. */
export function resolveServicePricing(
  service: ServicePricingInput,
  rawQuantity?: number | null,
): ResolvedServicePricing {
  const unitPrice = Number(service?.price) || 0;
  const billingUnit = (service?.billingUnit as string) || 'flat';
  const unitLabel = (service?.unitLabel || '').trim() || defaultUnitLabel(billingUnit);

  const raw = Number(rawQuantity);
  let units = Number.isFinite(raw) && raw > 0 ? raw : 1;

  const minimum = Number(service?.minimumUnits) || 0;
  if (minimum > 0 && units < minimum) units = minimum;

  if (billingUnit === 'per_hour') {
    const increment = Math.floor(Number(service?.unitIncrement) || 0);
    if (increment > 0) units = (Math.ceil((units * 60) / increment) * increment) / 60;
  }

  const quantity = round2(units);
  return { unitPrice, quantity, unitLabel, amount: round2(unitPrice * quantity) };
}

/** Formats a unit label for display, e.g. "3 hr" or "3 hour". */
export function formatQuantity(quantity: number | string | null | undefined, unitLabel?: string | null): string {
  const qty = Number(quantity || 0);
  const qtyText = Number.isInteger(qty) ? String(qty) : String(round2(qty));
  return unitLabel ? `${qtyText} ${unitLabel}` : qtyText;
}

/**
 * Largest quantity the add-services endpoint accepts (mirrors the API's
 * `@Max(100000)` guard). Keeps the stepper from submitting a value the server
 * would reject.
 */
export const MAX_SERVICE_QUANTITY = 100000;

export interface ServiceQuantityBounds {
  min: number;
  max: number;
  step: number;
}

/**
 * Stepper bounds for one service line. Time-billed services step by their
 * configured rounding increment (or half an hour), while piece/day/flat
 * services step by one. The configured minimum-units value is never undercut.
 */
export function serviceQuantityBounds(service: ServicePricingInput): ServiceQuantityBounds {
  const minimum = Number(service?.minimumUnits) || 0;
  if (service?.billingUnit === 'per_hour') {
    const increment = Number(service?.unitIncrement) || 0;
    const step = increment > 0 ? round2(increment / 60) : 0.5;
    return {
      min: Math.min(MAX_SERVICE_QUANTITY, Math.max(step, round2(minimum))),
      max: MAX_SERVICE_QUANTITY,
      step,
    };
  }
  return {
    min: Math.min(MAX_SERVICE_QUANTITY, Math.max(1, round2(minimum))),
    max: MAX_SERVICE_QUANTITY,
    step: 1,
  };
}
